import { useState, useEffect, useCallback, useRef } from 'react';
import ticketApi from '../services/ticketApi';
import { 
  LOADING_STATES, 
  PAGINATION_DEFAULTS, 
  ERROR_MESSAGES,
  AUTO_REFRESH_INTERVAL 
} from '../constants';

/**
 * Custom hook for managing ticket dashboard data and state
 * 
 * @param {Object} initialFilters - Initial filter values
 * @param {boolean} autoRefresh - Whether to enable auto-refresh (default: false)
 * @returns {Object} Dashboard state and methods
 */
const useTicketDashboard = (initialFilters = {}, autoRefresh = false) => {
  // State management
  const [tickets, setTickets] = useState(null);
  const [statistics, setStatistics] = useState(null);
  const [loading, setLoading] = useState(LOADING_STATES.IDLE);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({
    page: PAGINATION_DEFAULTS.PAGE,
    limit: PAGINATION_DEFAULTS.LIMIT,
    ...initialFilters
  });

  // Refs for cleanup and current values
  const refreshIntervalRef = useRef(null);
  const abortControllerRef = useRef(null);
  const filtersRef = useRef(filters);
  const initialFiltersRef = useRef(initialFilters);

  /**
   * Handle API errors with user-friendly messages
   */
  const handleError = useCallback((err) => {
    console.error('Dashboard API Error:', err);
    
    if (err.response?.status === 401) {
      setError(ERROR_MESSAGES.UNAUTHORIZED);
    } else if (err.response?.status === 404) {
      setError(ERROR_MESSAGES.NOT_FOUND);
    } else if (err.response?.status >= 500) {
      setError(ERROR_MESSAGES.SERVER_ERROR);
    } else if (err.code === 'NETWORK_ERROR') {
      setError(ERROR_MESSAGES.NETWORK_ERROR);
    } else {
      setError(err.message || ERROR_MESSAGES.FETCH_FAILED);
    }
  }, []);

  /**
   * Fetch tickets with current filters
   */
  const fetchTickets = useCallback(async (customFilters = {}) => {
    // Cancel previous request if still pending
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    abortControllerRef.current = new AbortController();

    setLoading(LOADING_STATES.LOADING);
    setError(null);

    try {
      const queryParams = { ...filtersRef.current, ...customFilters };

      // Remove empty values
      const cleanParams = Object.entries(queryParams)
        .filter(([_, value]) => value !== '' && value !== null && value !== undefined)
        .reduce((acc, [key, value]) => ({ ...acc, [key]: value }), {});

      const result = await ticketApi.getTickets(cleanParams);

      if (!abortControllerRef.current.signal.aborted) {
        setTickets(result);
        setLoading(LOADING_STATES.SUCCESS);
      }
    } catch (err) {
      if (!abortControllerRef.current.signal.aborted) {
        handleError(err);
        setLoading(LOADING_STATES.ERROR);
      }
    }
  }, [handleError]); // Only depend on handleError

  /**
   * Fetch dashboard statistics
   */
  const fetchStatistics = useCallback(async (customFilters = {}) => {
    try {
      const queryParams = { ...filtersRef.current, ...customFilters };

      // Remove pagination params for statistics
      const { page, limit, metaStatus,  ...statsFilters } = queryParams;

      
      const result = await ticketApi.getDashboardStatistics(statsFilters);
      setStatistics(result);
    } catch (err) {
      console.error('Failed to fetch statistics:', err);
      // Don't set error state for statistics failure
    }
  }, []); // No dependencies needed



  /**
   * Update filters and refetch data
   */
  const updateFilters = useCallback((newFilters) => {
    setFilters(prev => ({ ...prev, ...newFilters }));
  }, []);

  /**
   * Reset filters to initial state
   */
  const resetFilters = useCallback(() => {
    setFilters({
      page: PAGINATION_DEFAULTS.PAGE,
      limit: PAGINATION_DEFAULTS.LIMIT,
      ...initialFiltersRef.current
    });
  }, []); // No dependencies needed



  /**
   * Go to specific page
   */
  const goToPage = useCallback((page) => {
    updateFilters({ page });
  }, [updateFilters]);

  /**
   * Change page size
   */
  const changePageSize = useCallback((limit) => {
    updateFilters({ page: 1, limit });
  }, [updateFilters]);

  /**
   * Setup auto-refresh
   */
  useEffect(() => {
    if (autoRefresh) {
      refreshIntervalRef.current = setInterval(() => {
        // Use the stable functions for auto-refresh
        fetchTickets();
        fetchStatistics();
      }, AUTO_REFRESH_INTERVAL);

      return () => {
        if (refreshIntervalRef.current) {
          clearInterval(refreshIntervalRef.current);
        }
      };
    }
  }, [autoRefresh]); // Only depend on autoRefresh flag

  /**
   * Update filters ref when filters change
   */
  useEffect(() => {
    filtersRef.current = filters;
  }, [filters]);

  /**
   * Fetch data when filters change
   */
  useEffect(() => {
    fetchTickets();
    fetchStatistics();
  }, [filters]); // Only depend on filters, functions are stable with refs

  /**
   * Cleanup on unmount
   */
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      if (refreshIntervalRef.current) {
        clearInterval(refreshIntervalRef.current);
      }
    };
  }, []);

  return {
    // Data
    tickets,
    statistics,
    filters,

    // State
    loading,
    error,
    isLoading: loading === LOADING_STATES.LOADING,

    // Methods
    fetchTickets,
    fetchStatistics,
    updateFilters,
    resetFilters,
    
    goToPage,
    changePageSize,

    // Computed values
    totalPages: tickets?.totalPages || 0,
    totalItems: tickets?.total || 0,
    currentPage: tickets?.page || 1,
    hasNextPage: tickets ? tickets.page < tickets.totalPages : false,
    hasPrevPage: tickets ? tickets.page > 1 : false
  };
};

export { useTicketDashboard as default };
export { useTicketDashboard };
